/**
 * decorations.ts — visual-only detail geometry for surf maps.
 *
 * All returned meshes are named with the `detail_` prefix and are NEVER
 * added to colliders.  Three feature types:
 *   rails   — thin box strips along the base edges of each lane segment
 *   pillars — cylindrical support columns from the ground plane upward
 *   skirts  — thin vertical walls hanging below the base edges
 *
 * Geometry is built in each segment's local space (same length as the prism,
 * including JOINT_OVERLAP) and the segment's rotation/position is copied
 * directly so rail/skirt transforms exactly match the lane surface.
 */
import {
  Box3,
  BoxGeometry,
  CylinderGeometry,
  Mesh,
  type MeshStandardMaterial,
  Vector3,
} from 'three';
import type { DecorOptions } from './types';

// Joint overlap duplicated here to avoid a circular dep on laneBuilder.
const JOINT_OVERLAP = 32;

// ──────────────────────────────────────────────────────────────────────────────
// Resolved option helpers
// ──────────────────────────────────────────────────────────────────────────────

interface ResolvedRails {
  height: number;
}
interface ResolvedPillars {
  spacing: number;
  radius: number;
}
interface ResolvedSkirts {
  depth: number;
}

function resolveRails(opt: DecorOptions['rails']): ResolvedRails | null {
  if (opt === false || opt === undefined) return null;
  if (opt === true) return { height: 48 };
  return { height: opt.height };
}

function resolvePillars(opt: DecorOptions['pillars']): ResolvedPillars | null {
  if (opt === false || opt === undefined) return null;
  if (opt === true) return { spacing: 384, radius: 48 };
  return { spacing: opt.spacing, radius: opt.radius };
}

function resolveSkirts(opt: DecorOptions['skirts']): ResolvedSkirts | null {
  if (opt === false || opt === undefined) return null;
  if (opt === true) return { depth: 220 };
  return { depth: opt.depth };
}

// ──────────────────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Generate detail meshes for a single stage.
 *
 * @param stageIndex  Stage index (for name prefixes)
 * @param laneMeshes  The lane segment meshes already positioned/rotated by buildLane
 * @param lane        The LaneSpec for this stage (width used for lateral offsets)
 * @param decor       Resolved decor options for this stage
 * @param ground      The ground plane y coordinate (used for pillar height)
 * @param material    Shared decor material
 */
export function generateDecor(
  stageIndex: number,
  laneMeshes: Mesh[],
  lane: { width: number },
  decor: DecorOptions,
  ground: { y: number },
  material: MeshStandardMaterial,
): Mesh[] {
  const results: Mesh[] = [];

  const rails = resolveRails(decor.rails);
  const pillars = resolvePillars(decor.pillars);
  const skirts = resolveSkirts(decor.skirts);

  for (let mi = 0; mi < laneMeshes.length; mi++) {
    const seg = laneMeshes[mi];
    if (seg === undefined) continue;

    // Determine the prism length from the segment geometry (includes JOINT_OVERLAP).
    // The geometry runs from z=0 to z=length in local space.
    seg.geometry.computeBoundingBox();
    const bb = seg.geometry.boundingBox;
    if (bb === null) continue;
    const segLength = bb.max.z - bb.min.z; // == original segment.length + JOINT_OVERLAP

    const halfW = lane.width / 2;

    // ── Rails ────────────────────────────────────────────────────────────────
    if (rails !== null) {
      const rh = rails.height;
      // Rail is outboard of base edge by 8u for clearance, 16u wide.
      // Position: local x = ±(halfW + 8 + 8)  (center of the 16-wide box)
      //           local y = rh * 0.4 - rh/2    (top at +rh*0.4, center = top - rh/2)
      // The boxGeometry is centered; we want top at y = rh*0.4.
      const railCenterY = rh * 0.4 - rh / 2; // = -rh * 0.1
      // Outboard edge at ±(halfW + 8); center of 16-wide box at ±(halfW + 8 + 8)
      const railXOuter = halfW + 16; // center x of the 16-wide rail

      for (const side of [-1, 1] as const) {
        const railGeo = new BoxGeometry(16, rh, segLength);
        const rail = new Mesh(railGeo, material);
        // Build in segment local space, then copy segment transform
        rail.rotation.order = seg.rotation.order;
        rail.rotation.copy(seg.rotation);
        // We need to apply the local offset in segment space:
        // local point (side*railXOuter, railCenterY, segLength/2)
        const localOffset = new Vector3(side * railXOuter, railCenterY, segLength / 2);
        localOffset.applyQuaternion(seg.quaternion);
        rail.position.set(
          seg.position.x + localOffset.x,
          seg.position.y + localOffset.y,
          seg.position.z + localOffset.z,
        );
        const sideName = side === 1 ? 'r' : 'l';
        rail.name = `detail_rail_s${stageIndex}_${mi}_${sideName}`;
        results.push(rail);
      }
    }

    // ── Skirts ───────────────────────────────────────────────────────────────
    if (skirts !== null) {
      const depth = skirts.depth;
      // Thin wall (8 thick) hanging below y=0, centered at y = -depth/2
      // Outboard by 2u: local x = ±(halfW + 2 + 4)  (center of 8-wide box)
      const skirtXOuter = halfW + 6; // = halfW + 2 (gap) + 4 (half of 8-wide box)
      const skirtCenterY = -depth / 2;

      for (const side of [-1, 1] as const) {
        const skirtGeo = new BoxGeometry(8, depth, segLength);
        const skirt = new Mesh(skirtGeo, material);
        skirt.rotation.order = seg.rotation.order;
        skirt.rotation.copy(seg.rotation);
        const localOffset = new Vector3(side * skirtXOuter, skirtCenterY, segLength / 2);
        localOffset.applyQuaternion(seg.quaternion);
        skirt.position.set(
          seg.position.x + localOffset.x,
          seg.position.y + localOffset.y,
          seg.position.z + localOffset.z,
        );
        const sideName = side === 1 ? 'r' : 'l';
        skirt.name = `detail_skirt_s${stageIndex}_${mi}_${sideName}`;
        results.push(skirt);
      }
    }

    // ── Pillars ──────────────────────────────────────────────────────────────
    if (pillars !== null) {
      const { spacing, radius } = pillars;
      // Pillar tops are at local y=0 (the base of the prism).
      // Place at t offsets along the segment length (excluding overlap).
      const segBase = segLength - JOINT_OVERLAP; // nominal segment length
      // If the segment is shorter than the spacing, skip pillars (avoids excessive
      // density on heavily subdivided curve segments).
      if (segBase < spacing) continue;
      const count = Math.max(1, Math.floor(segBase / spacing));
      const step = segBase / count;

      seg.updateWorldMatrix(true, false);

      for (let pi = 0; pi < count; pi++) {
        const t = step * 0.5 + pi * step; // mid-interval placement
        for (const side of [-1, 1] as const) {
          const localX = (side * lane.width) / 4; // ±width/4 under the faces

          // World position of the pillar top (base of prism at local y=0)
          const localTop = new Vector3(localX, 0, t);
          const worldTop = localTop.clone().applyMatrix4(seg.matrixWorld);

          const h = worldTop.y - ground.y;
          if (h <= 256) continue; // skip near-ground pillars

          const pillarCenterY = ground.y + h / 2;
          // Use 6 radial segments for low poly count (readable at game scale, small in glb)
          const pillarMesh = new Mesh(new CylinderGeometry(radius, radius * 1.15, h, 6), material);
          pillarMesh.position.set(worldTop.x, pillarCenterY, worldTop.z);
          const sideName = side === 1 ? 'r' : 'l';
          pillarMesh.name = `detail_pillar_s${stageIndex}_${mi}_${pi}_${sideName}`;
          results.push(pillarMesh);
        }
      }
    }
  }

  return results;
}

/**
 * Merge map-level decor options with stage-level overrides.
 * - If both map and stage decor are absent/undefined → return null (all off).
 * - Merge per-key: stage key takes precedence over map key.
 */
export function mergeDecor(
  mapDecor: DecorOptions | undefined,
  stageDecor: Partial<DecorOptions> | undefined,
): DecorOptions | null {
  if (mapDecor === undefined && stageDecor === undefined) return null;

  const base: DecorOptions = mapDecor ?? { rails: false, pillars: false, skirts: false };
  if (stageDecor === undefined) return base;

  return {
    rails: stageDecor.rails !== undefined ? stageDecor.rails : base.rails,
    pillars: stageDecor.pillars !== undefined ? stageDecor.pillars : base.pillars,
    skirts: stageDecor.skirts !== undefined ? stageDecor.skirts : base.skirts,
  };
}

/** Compute the world-space bounding box of a mesh (updates world matrix first). */
export function meshWorldAABB(mesh: Mesh): Box3 {
  mesh.updateWorldMatrix(true, true);
  return new Box3().setFromObject(mesh);
}
